import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Trash2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Alert, AlertDescription } from '../ui/alert';
import ConfirmDialog from '../ui/ConfirmDialog';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { useFormAutoSave } from '../../hooks/useFormAutoSave';
import { Category, CategoryGroup } from '../../types';

// Un groupe de catégories dans un panneau latéral (2026-09-27). Un groupe est
// une COMBINAISON de catégories pour une heat : au moins deux, sinon ce n'est
// pas un groupe — la règle est aussi tenue par la base (`min_two_categories`).
//
// Sauvegarde automatique (règle du 22/09) : texte au blur, cases et
// interrupteur au clic. Décocher jusqu'à une seule catégorie n'écrit rien :
// l'erreur reste sous la liste, la base garde le dernier groupe valide.

export interface CategoryGroupFormValues {
  name: string;
  description: string;
  category_ids: string[];
  is_active: boolean;
}

export type CategoryGroupPatch = Partial<CategoryGroupFormValues>;

function toValues(g: CategoryGroup | null): CategoryGroupFormValues {
  return { name: g?.name ?? '', description: g?.description ?? '', category_ids: g?.category_ids ?? [], is_active: g?.is_active ?? true };
}

const toPatch = (fields: (keyof CategoryGroupFormValues)[], v: CategoryGroupFormValues): CategoryGroupPatch => {
  const p: CategoryGroupPatch = {};
  if (fields.includes('name')) p.name = v.name.trim();
  if (fields.includes('description')) p.description = v.description;
  if (fields.includes('category_ids')) p.category_ids = v.category_ids;
  if (fields.includes('is_active')) p.is_active = v.is_active;
  return p;
};

interface Props {
  open: boolean;
  group: CategoryGroup | null;
  categories: Category[];
  saving: boolean;
  serverError: string;
  /** Création seulement. */
  onSubmit: (values: CategoryGroupFormValues) => void;
  /** Édition : écriture partielle, immédiate. Lève sur refus. */
  onPatch: (patch: CategoryGroupPatch) => Promise<void>;
  onRemove: () => void;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function CategoryGroupSheet({ open, group, categories, saving, serverError, onSubmit, onPatch, onRemove, onClose, onDirtyChange, onPrev, onNext }: Props) {
  const { t } = useTranslation();
  const editing = !!group;

  const schema = useMemo(() => z.object({
    name: z.string().trim().min(1, t('categoryGroups.validation.nameRequired')),
    description: z.string(),
    category_ids: z.array(z.string()).min(2, t('categoryGroups.validation.minCategories')),
    is_active: z.boolean(),
  }), [t]);

  const form = useForm<CategoryGroupFormValues>({ resolver: zodResolver(schema), defaultValues: toValues(group), mode: 'onTouched' });
  const autoSave = useFormAutoSave<CategoryGroupFormValues, CategoryGroupPatch>(form, {
    enabled: editing,
    save: onPatch,
    toPatch,
    onError: (err: any) => toast.error(err?.response?.data?.detail || t('errors.generic')),
  });
  const { saveFields } = autoSave;

  useEffect(() => {
    autoSave.reset(toValues(group));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, open]);

  const isDirty = !editing && form.formState.isDirty;
  useEffect(() => { onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);

  const [confirmDiscard, setConfirmDiscard] = useState<null | (() => void)>(null);
  const guard = (action: () => void) => (isDirty ? setConfirmDiscard(() => action) : action());

  return (
    <>
      <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) guard(onClose); }}>
        <SheetContent side="right" hideClose onInteractOutside={(e) => e.preventDefault()} className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[520px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full min-h-0 flex-col" noValidate>
              <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SheetDescription className="text-xs">
                    {group ? t('categoryGroups.categoriesCount', { count: group.category_ids.length }) : t('categoryGroups.sheet.createHint')}
                  </SheetDescription>
                  <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">
                    {group ? group.name : t('categoryGroups.createGroup')}
                  </SheetTitle>
                </div>
                {group && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={() => onPrev && guard(onPrev)}><ChevronUp size={16} /></Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={() => onNext && guard(onNext)}><ChevronDown size={16} /></Button>
                  </>
                )}
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => guard(onClose)}><X size={16} /></Button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('categoryGroups.groupName')}</FormLabel>
                    <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveFields('name'); }} placeholder={t('categoryGroups.groupNamePlaceholder')} autoFocus={!group} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('categoryGroups.description')}</FormLabel>
                    <FormControl><Textarea {...field} onBlur={() => { field.onBlur(); void saveFields('description'); }} rows={2} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="category_ids" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('categoryGroups.categoriesLabel')}</FormLabel>
                    <div className="flex flex-col gap-2">
                      {categories.map((c) => (
                        <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                          <Checkbox
                            checked={field.value.includes(c.id)}
                            onCheckedChange={(v) => { field.onChange(v === true ? [...field.value, c.id] : field.value.filter((id) => id !== c.id)); void saveFields('category_ids'); }}
                          />
                          {c.name}
                        </label>
                      ))}
                      {categories.length === 0 && <span className="text-sm text-muted-foreground">{t('categories.noCategories')}</span>}
                    </div>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="is_active" render={({ field }) => (
                  <FormItem className="flex items-center gap-3 space-y-0">
                    <FormControl><Switch checked={field.value} onCheckedChange={(v) => { field.onChange(v); void saveFields('is_active'); }} /></FormControl>
                    <div>
                      <FormLabel>{t('categoryGroups.groupActive')}</FormLabel>
                      <FormDescription>{t('categoryGroups.sheet.activeHint')}</FormDescription>
                    </div>
                  </FormItem>
                )} />
                {serverError && <Alert variant="destructive"><AlertDescription>{serverError}</AlertDescription></Alert>}
              </div>

              <div className="flex items-center gap-2 border-t border-white/[0.08] px-6 py-4">
                <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                  {editing && <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />}
                </span>
                {group ? (
                  <>
                    <Button type="button" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={onRemove} disabled={saving}>
                      <Trash2 size={16} />{t('common.delete')}
                    </Button>
                    <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
                  </>
                ) : (
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="animate-spin" />}
                    {t('common.create')}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard !== null}
        onOpenChange={(o) => { if (!o) setConfirmDiscard(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { const next = confirmDiscard; setConfirmDiscard(null); form.reset(toValues(group)); next?.(); }}
      />
    </>
  );
}
